import { useEffect } from 'react';
import { HeaderNav } from '~/landing_page/components/HeaderNav/HeaderNav';
import type { HeaderNavProps } from '~/landing_page/components/HeaderNav/HeaderNav';
import { HeroSection } from '~/landing_page/components/HeroSection/HeroSection';
import type { HeroSectionProps } from '~/landing_page/components/HeroSection/HeroSection';
import { TrustedByLogos } from '~/landing_page/components/TrustedByLogos/TrustedByLogos';
import type { TrustedByLogosProps } from '~/landing_page/components/TrustedByLogos/TrustedByLogos';
import { ProblemSection } from '~/landing_page/components/ProblemSection/ProblemSection';
import type { ProblemSectionProps } from '~/landing_page/components/ProblemSection/ProblemSection';
import { FeaturesSection } from '~/landing_page/components/FeaturesSection/FeaturesSection';
import type { FeaturesSectionProps } from '~/landing_page/components/FeaturesSection/FeaturesSection';
// The card-grid UseCasesSection stays parked pending real generated decks — see
// UseCasesSection/USE_CASE_DECKS.md. WhoItsForSection is the Figma 262:9538 replacement and takes
// over the #use-cases anchor the header nav points at.
import { WhoItsForSection } from '~/landing_page/components/WhoItsForSection';
import type { WhoItsForSectionProps } from '~/landing_page/components/WhoItsForSection';
import { PricingSection } from '~/landing_page/components/PricingSection/PricingSection';
import type { PricingSectionProps } from '~/landing_page/components/PricingSection/PricingSection';
import { PrivacySection } from '~/landing_page/components/PrivacySection/PrivacySection';
import type { PrivacySectionProps } from '~/landing_page/components/PrivacySection/PrivacySection';
import { FAQSection } from '~/landing_page/components/FAQSection';
import type { FAQSectionProps } from '~/landing_page/components/FAQSection';
import { CTASection } from '~/landing_page/components/CTASection/CTASection';
import type { CTASectionProps } from '~/landing_page/components/CTASection/CTASection';
import { LogoSection } from '~/landing_page/components/LogoSection/LogoSection';
import type { LogoSectionProps } from '~/landing_page/components/LogoSection/LogoSection';
import { FooterSection } from '~/landing_page/components/FooterSection/FooterSection';
import type { FooterSectionProps } from '~/landing_page/components/FooterSection/FooterSection';
import { CookieBanner } from '~/landing_page/components/CookieBanner/CookieBanner';
import { cn } from '~/lib/utils';
import styles from './LandingPage.module.css';

const scrollToHashSection = () => {
  const raw = window.location.hash.slice(1);
  if (!raw) return;
  const el = document.getElementById(decodeURIComponent(raw));
  if (!el) return;
  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};

export interface LandingPageProps {
  className?: string;
  headerNavProps?: HeaderNavProps;
  heroSectionProps?: HeroSectionProps;
  trustedByLogosProps?: TrustedByLogosProps;
  problemSectionProps?: ProblemSectionProps;
  featuresSectionProps?: FeaturesSectionProps;
  whoItsForSectionProps?: WhoItsForSectionProps;
  pricingSectionProps?: PricingSectionProps;
  privacySectionProps?: PrivacySectionProps;
  faqSectionProps?: FAQSectionProps;
  ctaSectionProps?: CTASectionProps;
  logoSectionProps?: LogoSectionProps;
  footerSectionProps?: FooterSectionProps;
}

export function LandingPage({
  className,
  headerNavProps,
  heroSectionProps,
  trustedByLogosProps,
  problemSectionProps,
  featuresSectionProps,
  whoItsForSectionProps,
  pricingSectionProps,
  privacySectionProps,
  faqSectionProps,
  ctaSectionProps,
  logoSectionProps,
  footerSectionProps,
}: LandingPageProps) {
  useEffect(() => {
    scrollToHashSection();
    window.addEventListener('hashchange', scrollToHashSection);
    return () => window.removeEventListener('hashchange', scrollToHashSection);
  }, []);

  return (
    <div className={cn(styles.root, className)} data-name="LandingPage">
      <HeaderNav {...headerNavProps} />
      <HeroSection {...heroSectionProps} />
      <TrustedByLogos {...trustedByLogosProps} />
      <ProblemSection variant="desktop" {...problemSectionProps} />
      <FeaturesSection
        {...featuresSectionProps}
        id="features"
        className={cn('scroll-section', featuresSectionProps?.className)}
      />
      <WhoItsForSection
        {...whoItsForSectionProps}
        id="use-cases"
        className={cn('scroll-section', whoItsForSectionProps?.className)}
      />
      <PricingSection
        {...pricingSectionProps}
        id="pricing"
        className={cn('scroll-section', pricingSectionProps?.className)}
      />
      <PrivacySection
        {...privacySectionProps}
        id="privacy"
        className={cn('scroll-section', privacySectionProps?.className)}
      />
      <FAQSection
        {...faqSectionProps}
        id="faq"
        className={cn('scroll-section', faqSectionProps?.className)}
      />
      <CTASection {...ctaSectionProps} />
      <LogoSection {...logoSectionProps} />
      <FooterSection {...footerSectionProps} />
      <CookieBanner />
    </div>
  );
}

LandingPage.displayName = 'LandingPage';
